import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import type { RfiFile } from 'scaleflow/services/rfi';
import { formatBytes } from 'scaleflow/utils/manual-repo-archives';
import DocumentText from 'scaleflow/components/tailwind-icons/document-text';
import ArrowDownTray from 'scaleflow/components/tailwind-icons/arrow-down-tray';
import Trash from 'scaleflow/components/tailwind-icons/trash';

export interface RfiFileListSignature {
  Element: HTMLUListElement;
  Args: {
    Named: {
      files: RfiFile[];
      onDownload: (fileId: string) => void;
      onRemove: (fileId: string) => void;
    };
  };
}

export default class RfiFileList extends Component<RfiFileListSignature> {
  get rows() {
    return this.args.files.map((file) => ({
      ...file,
      sizeText: formatBytes(file.size),
      isUploading: file.status === 'uploading',
      isFailed: file.status === 'failed',
      isUploaded: file.status === 'uploaded',
    }));
  }

  <template>
    <ul class="flex flex-col gap-2" ...attributes>
      {{#each this.rows key="id" as |file|}}
        <li
          class="flex items-center gap-3 rounded-lg border bg-white py-1 pl-3 pr-1
            {{if file.isFailed 'border-red-300' 'border-gray-200'}}"
        >
          <DocumentText class="h-5 w-5 shrink-0 text-gray-500" />
          <div class="flex min-w-0 flex-1 flex-col gap-1 py-1">
            <span class="truncate text-sm text-gray-900">{{file.name}}</span>
            {{#if file.isUploading}}
              <progress
                class="h-1.5 w-full accent-primary-600"
                max={{file.size}}
                value={{file.loaded}}
                aria-label={{t "rfi.uploading-file" name=file.name}}
              ></progress>
            {{else if file.isFailed}}
              <span class="text-xs font-medium text-red-700">{{t
                  "rfi.upload-failed"
                }}</span>
            {{/if}}
          </div>
          <span class="shrink-0 text-xs text-gray-600">{{file.sizeText}}</span>
          {{#if file.isUploaded}}
            <button
              type="button"
              class="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              aria-label={{t "rfi.download-file" name=file.name}}
              {{on "click" (fn @onDownload file.id)}}
            >
              <ArrowDownTray class="h-4 w-4" />
            </button>
          {{/if}}
          {{#if file.isUploading}}
            {{! Nothing to remove yet: no way to cancel an in-flight upload. }}
            <span class="h-11 w-11 shrink-0" aria-hidden="true"></span>
          {{else}}
            <button
              type="button"
              class="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              aria-label={{t "rfi.remove-file" name=file.name}}
              {{on "click" (fn @onRemove file.id)}}
            >
              <Trash class="h-4 w-4" />
            </button>
          {{/if}}
        </li>
      {{/each}}
    </ul>
  </template>
}
